'use client';

import { useEffect, useRef, useState } from 'react';
import { usePondPathname } from './usePondPathname';
import { PondSceneV3 } from '@/lib/koi-pond-v3/scene/PondSceneV3';
import { SandSceneV3 } from '@/lib/sand-v3/scene/SandSceneV3';
import { StaticPondBackground } from './StaticPondBackground';
import type { SceneId } from './types';

// Route-aware mounting of pond vs. sand scenes with an 800ms cross-fade
// during transitions. We snapshot the outgoing scene element into a ref so
// Next.js's route swap doesn't unmount the previous scene's React tree
// before the fade timer fires — that was the Wave D audit finding (the
// swap finished in ~200ms instead of 800).
//
// Dispatches a 'scene:transition' CustomEvent on cross-fade kick so the
// cursor (LanternCursor) can briefly desaturate its trailing ring.

const CROSSFADE_MS = 800;

function renderScene(id: SceneId | null): React.ReactNode {
  // Phase 12.2 cutover — production pond is now the v3 moonlit scene, mounted
  // in background mode (window-bound input, camera drift, no OrbitControls).
  // No dim overlay: v3's near-black water gives the cream hero text natural
  // contrast, so the v2 heroDim quad is retired here. (/koi-debug still mounts
  // v2 directly for its own measurement surface until Phase 13.4 deletes v2.)
  if (id === 'pond') return <PondSceneV3 mode="background" />;
  if (id === 'sand') return <SandSceneV3 />;
  if (id === 'pond-still') return <StaticPondBackground />;
  return null;
}

export function BackgroundProvider() {
  const target = usePondPathname();
  const [active, setActive] = useState<SceneId | null>(target);
  const [outgoing, setOutgoing] = useState<SceneId | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  // Snapshot of the outgoing scene's element. Captured at transition
  // start so it survives Next's route unmount.
  const outgoingNodeRef = useRef<React.ReactNode>(null);
  const outgoingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (target === active) return;
    if (outgoingTimerRef.current) clearTimeout(outgoingTimerRef.current);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('scene:transition', { detail: { to: target } }),
      );
      document.documentElement.dataset.sceneTransitioning = '1';
    }
    // Freeze the previous scene's element so the route swap doesn't
    // unmount it mid-fade.
    outgoingNodeRef.current = renderScene(active);
    setOutgoing(active);
    setActive(target);
    setTransitioning(true);
    outgoingTimerRef.current = setTimeout(() => {
      setOutgoing(null);
      outgoingNodeRef.current = null;
      setTransitioning(false);
      if (typeof document !== 'undefined') {
        delete document.documentElement.dataset.sceneTransitioning;
      }
    }, CROSSFADE_MS + 100);
    return () => {
      if (outgoingTimerRef.current) clearTimeout(outgoingTimerRef.current);
    };
  }, [target, active]);

  return (
    <>
      {outgoing && outgoing !== active && outgoingNodeRef.current && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
            opacity: 0,
            transition: `opacity ${CROSSFADE_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
          }}
        >
          {outgoingNodeRef.current}
        </div>
      )}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 1,
          transition: transitioning
            ? `opacity ${CROSSFADE_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
            : 'none',
        }}
      >
        {renderScene(active)}
      </div>
    </>
  );
}
